import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalHeader,
  ModalCloseButton,
  useDisclosure,
  ModalFooter,
  useToast,
} from '@chakra-ui/react';
import React from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { SimpleDatePicker } from '~/packages/common-frontend/components/SimpleDatePicker';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  errorToastAdminConfig,
  successToastConfig,
} from '~/packages/common-frontend/constants';
import {
  GetInvoicePaymentInKindItemByIdDocumentGQLType,
  GetInvoicePaymentInKindItemsByInvoiceIdDocumentGQLType,
  useMarkInvoicePaymentInKindItemAsNotifiedMutation,
} from '~/packages/invoices/requests/invoices.gql.generated';

interface MarkAsNotifiedModalProps {
  invoicePaymentInKindItemId: string;
  modalControls: ReturnType<typeof useDisclosure>;
}

export const MarkAsNotifiedModal = ({
  invoicePaymentInKindItemId,
  modalControls,
}: MarkAsNotifiedModalProps) => {
  const toast = useToast();
  const [notifiedDate, setNotifiedDate] = React.useState<Date | null>(
    new Date(),
  );

  const [markAsNotified] = useMarkInvoicePaymentInKindItemAsNotifiedMutation({
    refetchQueries: [
      GetInvoicePaymentInKindItemsByInvoiceIdDocumentGQLType,
      GetInvoicePaymentInKindItemByIdDocumentGQLType,
    ],
  });

  const handleMarkAsNotifiedClick = async () => {
    try {
      if (notifiedDate) {
        await markAsNotified({
          variables: {
            invoicePaymentInKindItemId,
            notifiedDate: commonDate.dateToFullDateString(notifiedDate),
          },
        });
        toast({
          ...successToastConfig,
          title: `Marked invoice PIK as notified`,
        });
      } else {
        toast({
          ...successToastConfig,
          title: 'Failed to mark invoice PIK as notified',
        });
      }
    } catch (error) {
      toast({
        ...errorToastAdminConfig,
        title:
          'If you are unable to resolve the error, contact the Finley support team for assistance.',
      });
    }
  };

  return (
    <Modal {...modalControls}>
      <ModalOverlay />
      <ModalContent p="20px">
        <ModalHeader>
          <Typography variant="section-subtitle" trim>
            Select Notified Date
          </Typography>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleDatePicker
            dateFormat="MMMM dd, yyyy"
            selectedDate={notifiedDate}
            setSelectedDate={setNotifiedDate}
          />
        </ModalBody>
        <ModalFooter gap="0.5rem" mt="1rem">
          <Button
            colorScheme="v2-aurora"
            onClick={modalControls.onClose}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            colorScheme="v2-aurora"
            isDisabled={!notifiedDate}
            onClick={async () => {
              await handleMarkAsNotifiedClick();
              modalControls.onClose();
            }}
            variant="primary"
          >
            Submit
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
